import React, { useState, useEffect, Suspense } from 'react';
import { db, auth } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Stage } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { motion, AnimatePresence } from 'framer-motion';
import { useSubscription } from '../hooks/useSubscription';
import Paywall from './Paywall';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

// --- Diagnostic Shader Component ---
function HealthMesh({ modelUrl }) {
  const obj = useLoader(OBJLoader, modelUrl);
 
  return (
    <group>
      <primitive object={obj} scale={1.5}>
        <meshStandardMaterial
          color={GOLD}
          wireframe
          transparent
          opacity={0.15}
        />
      </primitive>
      <mesh position={[0, 0, 0]}>
        <planeGeometry args={[5, 5]} />
        <meshBasicMaterial color={GOLD} transparent opacity={0.03} side={2} />
      </mesh>
    </group>
  );
}

export default function BiometricService() {
  const [userData, setUserData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallReason, setPaywallReason] = useState(null);

  const navigate = useNavigate();
  const sub = useSubscription();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setUserData(d);

        // Dynamic alerts from Firestore + fallback system metrics
        const dynamicAlerts = d.healthAlerts?.map((alert, index) => ({
          id: `db-${index}`,
          type: alert.type || 'Structural',
          msg: alert.message || alert.msg,
          status: alert.isCritical ? 'Attention' : 'Stable'
        })) || [];

        const systemMetrics = [
          { id: 'sys-1', type: 'Biometric', msg: 'Cranial Symmetry: Verified 1:1', status: 'Stable' },
          { id: 'sys-2', type: 'Security', msg: 'Device IP Lock: Match Confirmed', status: 'Secure' }
        ];

        setAlerts([...dynamicAlerts, ...systemMetrics]);
      }
      setLoading(false);
    });

    return () => unsub();
  }, [navigate]);

  const handleRecalibrate = () => {
    // Respect quota — show paywall if needed
    if (!sub.isSuperAdmin && !sub.isSubscriptionActive && sub.lifetimeScansUsed >= 4) {
      setPaywallReason('free_exhausted');
      setShowPaywall(true);
      return;
    }
    navigate('/scan?force=true');
  };

  if (loading) {
    return <div style={fullPageLoader}>ENCRYPTING BIOMETRIC TUNNEL...</div>;
  }

  return (
    <div style={container}>
      {/* Paywall Overlay */}
      {showPaywall && (
        <Paywall
          reason={paywallReason}
          onClose={() => setShowPaywall(false)}
          redirectAfter="/biometric-health"
        />
      )}

      <header style={header}>
        <div style={identityBadge}>
          <div style={dot}></div>
          <span>SECURE BIOMETRIC LINK ACTIVE</span>
        </div>
        <h2 style={{ letterSpacing: '4px', fontWeight: '900', margin: '10px 0', textTransform: 'uppercase' }}>
          Health <span style={{ color: GOLD }}>Alerts</span>
        </h2>
      </header>

      <div style={mainGrid}>
        {/* 3D Visualizer */}
        <div style={visualizerCard}>
          <div style={viewTarget}>
            <Canvas camera={{ position: [0, 0, 5] }}>
              <Suspense fallback={null}>
                <Stage intensity={0.5} environment="city" contactShadow={false}>
                  {userData?.initialRenderUrl || userData?.lastScanUrl ? (
                    <HealthMesh modelUrl={userData.initialRenderUrl || userData.lastScanUrl} />
                  ) : (
                    <mesh>
                      <boxGeometry />
                      <meshBasicMaterial wireframe color={GOLD} />
                    </mesh>
                  )}
                </Stage>
              </Suspense>
              <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={0.8} />
            </Canvas>
            <div style={scanLineAnim}></div>
          </div>

          <div style={dataOverlay}>
            <p>NODE ID: {auth.currentUser?.uid?.slice(0, 12).toUpperCase()}</p>
            <p>ENCRYPTION: AES-256 BIOMETRIC</p>
            <p>IP LOCK: {userData?.lockedIP || 'ACTIVE'}</p>
          </div>
        </div>

        {/* Alerts Column */}
        <div style={alertColumn}>
          <AnimatePresence>
            {alerts.map((alert) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                style={alertCard}
              >
                <div style={statusDot(alert.status)}></div>
                <div style={{ flex: 1 }}>
                  <span style={typeLabel}>{alert.type}</span>
                  <p style={alertMsg}>{alert.msg}</p>
                </div>
                <span style={statusText}>{alert.status}</span>
              </motion.div>
            ))}
          </AnimatePresence>

          <div style={actionBox}>
            <h4 style={{ fontSize: '0.7rem', color: GOLD, marginBottom: '10px' }}>SYSTEM TOOLS</h4>
            <button onClick={handleRecalibrate} style={recalibrateBtn}>
              EMERGENCY RE-CALIBRATION
            </button>
            <p style={{ fontSize: '0.6rem', opacity: 0.5, marginTop: '10px', textAlign: 'center' }}>
              Use re-calibration if structural alerts show significant variance.
            </p>
          </div>

          <div style={privacyFooter}>
            <p style={{ margin: '0 0 5px 0' }}>DATA PROTOCOL: <strong>EARLY ACCESS HEALTH ALERTS</strong></p>
            <p style={{ opacity: 0.4, margin: 0 }}>Credits & Rewards: Deactivated by User</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- STYLES ---
const container = { padding: '40px', background: '#000', minHeight: '100vh', fontFamily: 'Montserrat, sans-serif', color: 'white' };
const header = { marginBottom: '40px' };
const identityBadge = { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.6rem', color: GOLD, fontWeight: 'bold' };
const dot = { width: '8px', height: '8px', background: GOLD, borderRadius: '50%', boxShadow: `0 0 10px ${GOLD}` };
const mainGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' };
const visualizerCard = { background: NAVY, borderRadius: '30px', padding: '20px', border: `1px solid ${GOLD}22`, position: 'relative', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' };
const viewTarget = { height: '450px', width: '100%', position: 'relative' };
const dataOverlay = { position: 'absolute', bottom: '25px', left: '25px', fontSize: '0.6rem', color: GOLD, opacity: 0.6, fontFamily: 'monospace', lineHeight: '1.5' };
const alertColumn = { display: 'flex', flexDirection: 'column', gap: '15px' };
const alertCard = { background: 'rgba(255,255,255,0.03)', padding: '20px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '15px', border: '1px solid rgba(255,255,255,0.08)' };
const typeLabel = { fontSize: '0.6rem', color: GOLD, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' };
const alertMsg = { margin: '5px 0 0', fontSize: '0.85rem', fontWeight: '500' };
const statusText = { fontSize: '0.7rem', opacity: 0.6, fontWeight: 'bold' };
const statusDot = (s) => ({
  width: '8px',
  height: '8px',
  background: (s === 'Optimal' || s === 'Secure' || s === 'Stable') ? '#4CAF50' : GOLD,
  borderRadius: '50%',
  boxShadow: (s === 'Optimal' || s === 'Stable') ? '0 0 8px #4CAF50' : 'none'
});
const actionBox = { background: 'rgba(184, 134, 11, 0.05)', padding: '20px', borderRadius: '20px', border: `1px solid ${GOLD}22`, marginTop: '10px' };
const recalibrateBtn = { width: '100%', padding: '15px', background: 'none', border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '50px', fontWeight: '900', fontSize: '0.7rem', cursor: 'pointer', transition: '0.3s' };
const privacyFooter = { marginTop: '20px', padding: '20px', borderRadius: '15px', background: 'rgba(0,0,0,0.3)', border: '1px solid #111', fontSize: '0.6rem', textAlign: 'center' };
const scanLineAnim = { position: 'absolute', width: '100%', height: '1px', background: GOLD, opacity: 0.4, top: '50%', boxShadow: `0 0 15px ${GOLD}` };
const fullPageLoader = { height: '100vh', display: 'grid', placeItems: 'center', background: '#000', color: GOLD, letterSpacing: '5px', fontSize: '0.7rem', fontWeight: 'bold' };
